import React, { useEffect, useState } from "react";
import { api, fmtNum } from "@/lib/api";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";

// Hoisted chart config — stable references so recharts memoization holds.
const CHART_MARGIN = { top: 10, right: 12, left: 0, bottom: 10 };
const X_AXIS_TICK = { fill: "#64748b", fontSize: 10 };
const Y_AXIS_TICK = { fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" };
const Y_AXIS_DOMAIN = ["auto", "auto"];
const TOOLTIP_CONTENT_STYLE = { background: "#131c2d", border: "1px solid #1e293b", fontSize: 11 };
const TOOLTIP_LABEL_STYLE = { color: "#94a3b8" };
const TOOLTIP_ITEM_STYLE = { fontFamily: "JetBrains Mono" };
const CHART_BOX_STYLE = { height: 320 };
const timeTickFormatter = (v) => v?.slice(11, 16);
const yTickFormatter = (v) => v.toFixed(0);

function FuturesChart({ underlying, candles }) {
  const data = candles.map((c) => ({
    ts: c.timestamp,
    close: c.close,
    high: c.high,
    low: c.low,
  }));
  // Compute EMA20 & EMA50 client-side for chart overlay
  function ema(arr, span) {
    const k = 2 / (span + 1);
    const out = [];
    let prev = null;
    for (const v of arr) {
      if (prev === null) prev = v;
      else prev = v * k + prev * (1 - k);
      out.push(prev);
    }
    return out;
  }
  const closes = data.map((d) => d.close);
  const ema20 = ema(closes, 20);
  const ema50 = ema(closes, 50);
  const enriched = data.map((d, i) => ({ ...d, ema20: ema20[i], ema50: ema50[i] }));

  return (
    <div className="surface rounded-sm p-4" data-testid={`chart-${underlying}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{underlying} Futures · 5m</div>
          <div className="text-lg font-mono">{fmtNum(data[data.length - 1]?.close)}</div>
        </div>
        <div className="flex gap-3 text-[10px] uppercase tracking-[0.15em] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-400" /> EMA20</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400" /> EMA50</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-slate-100" /> Close</span>
        </div>
      </div>
      <div style={CHART_BOX_STYLE}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={enriched} margin={CHART_MARGIN}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="2 2" />
            <XAxis
              dataKey="ts"
              tickFormatter={timeTickFormatter}
              tick={X_AXIS_TICK}
              minTickGap={40}
            />
            <YAxis
              domain={Y_AXIS_DOMAIN}
              tick={Y_AXIS_TICK}
              tickFormatter={yTickFormatter}
              width={60}
            />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
            />
            <Line type="monotone" dataKey="close" stroke="#f8fafc" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="ema20" stroke="#60a5fa" strokeWidth={1} dot={false} />
            <Line type="monotone" dataKey="ema50" stroke="#fbbf24" strokeWidth={1} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Markets() {
  const [nifty, setNifty] = useState([]);
  const [bn, setBn] = useState([]);
  const [tiles, setTiles] = useState({});

  useEffect(() => {
    (async () => {
      const [n, b, t] = await Promise.all([
        api.get("/market/futures/NIFTY"),
        api.get("/market/futures/BANKNIFTY"),
        api.get("/dashboard/tiles"),
      ]);
      setNifty(n.data.candles || []);
      setBn(b.data.candles || []);
      setTiles(t.data);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="markets-page">
      <h1 className="text-2xl font-medium tracking-tight">Markets</h1>
      <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
        NIFTY &amp; BANKNIFTY futures · completed 5-minute bars only
      </p>
      <FuturesChart underlying="NIFTY" candles={nifty} />
      <FuturesChart underlying="BANKNIFTY" candles={bn} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["NIFTY", "BANKNIFTY"].map((u) => {
          const t = tiles[u];
          if (!t) return null;
          return (
            <div key={u} className="surface rounded-sm p-4" data-testid={`indicator-panel-${u}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">{u} Indicators (latest)</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[
                  ["Close", t.last_close],
                  ["EMA20", t.ema20],
                  ["EMA50", t.ema50],
                  ["EMA20 (15m)", t.ema20_15],
                  ["EMA50 (15m)", t.ema50_15],
                  ["ADX14", t.adx14],
                  ["ATR14", t.atr14],
                  ["Volume Ratio", t.volume_ratio],
                  ["VWAP", t.vwap],
                  ["Opening High", t.opening_high],
                  ["Opening Low", t.opening_low],
                  ["Prior High12", t.prior_high12],
                  ["Prior Low12", t.prior_low12],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-800 pb-1">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-mono">{fmtNum(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
